/**
 * Localized SEO metadata for the public (indexable) pages.
 *
 * The app UI speaks 6 languages, so titles/descriptions shared on social
 * networks and indexed by search engines must follow the visitor's language
 * instead of being hardcoded in Italian.
 */
export const SEO_LOCALES = ["it", "en", "es", "fr", "de", "pt"] as const;
export type SeoLocale = (typeof SEO_LOCALES)[number];
export const DEFAULT_SEO_LOCALE: SeoLocale = "it";

export const SITE_URL = "https://lumen-ai.ltd";

export type SeoPage = "home" | "auth" | "terms" | "privacy";

type Entry = { title: string; description: string };

const COPY: Record<SeoPage, Record<SeoLocale, Entry>> = {
  home: {
    it: {
      title: "Lumen — Il tuo tutor AI per studiare meglio",
      description:
        "Lumen è il tutor AI che ti insegna come imparare. Studia meno, ricorda di più: metodi, mappe, quiz e planner personalizzati.",
    },
    en: {
      title: "Lumen — Your AI tutor for smarter studying",
      description:
        "Lumen is the AI tutor that teaches you how to learn. Study less, remember more: methods, mind maps, quizzes and a personal planner.",
    },
    es: {
      title: "Lumen — Tu tutor de IA para estudiar mejor",
      description:
        "Lumen es el tutor de IA que te enseña a aprender. Estudia menos y recuerda más: métodos, mapas, cuestionarios y planificador personalizado.",
    },
    fr: {
      title: "Lumen — Ton tuteur IA pour mieux réviser",
      description:
        "Lumen est le tuteur IA qui t'apprend à apprendre. Réviser moins, retenir plus : méthodes, cartes mentales, quiz et planning personnalisé.",
    },
    de: {
      title: "Lumen — Dein KI-Tutor für besseres Lernen",
      description:
        "Lumen ist der KI-Tutor, der dir zeigt, wie man lernt. Weniger lernen, mehr behalten: Methoden, Mindmaps, Quiz und persönlicher Lernplan.",
    },
    pt: {
      title: "Lumen — O teu tutor de IA para estudar melhor",
      description:
        "Lumen é o tutor de IA que te ensina a aprender. Estuda menos e recorda mais: métodos, mapas, quizzes e planeador personalizado.",
    },
  },
  auth: {
    it: {
      title: "Accedi o registrati — Lumen",
      description: "Accedi a Lumen o crea un account gratuito per iniziare a studiare con il tuo tutor AI personale.",
    },
    en: {
      title: "Sign in or sign up — Lumen",
      description: "Sign in to Lumen or create a free account to start studying with your personal AI tutor.",
    },
    es: {
      title: "Inicia sesión o regístrate — Lumen",
      description: "Entra en Lumen o crea una cuenta gratuita para empezar a estudiar con tu tutor de IA personal.",
    },
    fr: {
      title: "Se connecter ou s'inscrire — Lumen",
      description: "Connecte-toi à Lumen ou crée un compte gratuit pour réviser avec ton tuteur IA personnel.",
    },
    de: {
      title: "Anmelden oder registrieren — Lumen",
      description: "Melde dich bei Lumen an oder erstelle ein kostenloses Konto und lerne mit deinem persönlichen KI-Tutor.",
    },
    pt: {
      title: "Entrar ou registar — Lumen",
      description: "Entra no Lumen ou cria uma conta gratuita para estudar com o teu tutor de IA pessoal.",
    },
  },
  terms: {
    it: {
      title: "Termini e Condizioni — Lumen di Lerna AI",
      description:
        "Termini e condizioni d'uso di Lumen, il tutor AI di Lerna AI: account, uso accettabile, contenuti IA e responsabilità.",
    },
    en: {
      title: "Terms and Conditions — Lumen by Lerna AI",
      description:
        "Terms of use for Lumen, the AI tutor by Lerna AI: accounts, acceptable use, AI-generated content and liability.",
    },
    es: {
      title: "Términos y Condiciones — Lumen de Lerna AI",
      description:
        "Condiciones de uso de Lumen, el tutor de IA de Lerna AI: cuentas, uso aceptable, contenidos de IA y responsabilidad.",
    },
    fr: {
      title: "Conditions Générales — Lumen par Lerna AI",
      description:
        "Conditions d'utilisation de Lumen, le tuteur IA de Lerna AI : comptes, usage acceptable, contenus IA et responsabilité.",
    },
    de: {
      title: "Nutzungsbedingungen — Lumen von Lerna AI",
      description:
        "Nutzungsbedingungen von Lumen, dem KI-Tutor von Lerna AI: Konten, zulässige Nutzung, KI-Inhalte und Haftung.",
    },
    pt: {
      title: "Termos e Condições — Lumen da Lerna AI",
      description:
        "Termos de utilização do Lumen, o tutor de IA da Lerna AI: contas, uso aceitável, conteúdos de IA e responsabilidade.",
    },
  },
  privacy: {
    it: {
      title: "Informativa sulla Privacy — Lumen di Lerna AI",
      description: "Come Lerna AI raccoglie, utilizza e protegge i dati personali degli utenti di Lumen.",
    },
    en: {
      title: "Privacy Policy — Lumen by Lerna AI",
      description: "How Lerna AI collects, uses and protects the personal data of Lumen users.",
    },
    es: {
      title: "Política de Privacidad — Lumen de Lerna AI",
      description: "Cómo Lerna AI recoge, utiliza y protege los datos personales de los usuarios de Lumen.",
    },
    fr: {
      title: "Politique de confidentialité — Lumen par Lerna AI",
      description: "Comment Lerna AI collecte, utilise et protège les données personnelles des utilisateurs de Lumen.",
    },
    de: {
      title: "Datenschutzerklärung — Lumen von Lerna AI",
      description: "Wie Lerna AI die personenbezogenen Daten der Lumen-Nutzer erhebt, verwendet und schützt.",
    },
    pt: {
      title: "Política de Privacidade — Lumen da Lerna AI",
      description: "Como a Lerna AI recolhe, utiliza e protege os dados pessoais dos utilizadores do Lumen.",
    },
  },
};

export function normalizeSeoLocale(value: string | null | undefined): SeoLocale {
  if (!value) return DEFAULT_SEO_LOCALE;
  const lower = value.toLowerCase();
  for (const chunk of lower.split(",")) {
    const tag = chunk.split(";")[0]!.trim().slice(0, 2);
    const match = SEO_LOCALES.find((l) => l === tag);
    if (match) return match;
  }
  return DEFAULT_SEO_LOCALE;
}

export function seoCopy(page: SeoPage, lang: SeoLocale): Entry {
  return COPY[page][lang] ?? COPY[page][DEFAULT_SEO_LOCALE];
}

export function hreflangLinks(path: string) {
  const href = `${SITE_URL}${path}`;
  return [
    ...SEO_LOCALES.map((l) => ({ rel: "alternate", hrefLang: l, href })),
    { rel: "alternate", hrefLang: "x-default", href },
  ];
}

type MetaOptions = {
  path: string;
  ogType?: "website" | "article";
  robots?: string;
};

export function seoMeta(page: SeoPage, lang: SeoLocale, { path, ogType = "website", robots }: MetaOptions) {
  const { title, description } = seoCopy(page, lang);
  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: ogType },
    { property: "og:url", content: `${SITE_URL}${path}` },
    { property: "og:locale", content: lang },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    ...(robots ? [{ name: "robots", content: robots }] : []),
  ];
}
